import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService, apiClient } from "../../services/api/apiService";
import { getUserSports } from "../../services/api/sportsService";
import matchService from "../../services/api/matchService";
import directMessageService from "../../services/api/directMessageService";
import NotificationBell from "../ui/NotificationBell";
import PlayerSearch from "./PlayerSearch";
import InstallAppButton from "../pwa/InstallAppButton";
import MySchedule from "./MySchedule";
import { useWebSocket } from "../../contexts/WebSocketContext";
import { useAuth } from "../../contexts/AuthContext";
import logger from "../../utils/logger";
import followService from '../../services/api/followService';
import { getUserProfile } from '../../services/api/userService';
import teamService from '../../services/api/teamService';
import { normalizeLeaderboard } from '../../utils/teamStatsUtils';
import "../../styles/features/dashboard.css";
import "../../styles/components/websocket.css";
import SportsOnboardingModal, { ONBOARDING_KEY } from "../sports/SportsOnboardingModal";
import FilterBar from "../ui/FilterBar";
import { useUserFilters } from "../../contexts/UserFiltersContext";

function Dashboard({ token, onLogout }) {
  const [profile, setProfile] = useState({});
  const [userSports, setUserSports] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [liveMatchesCount, setLiveMatchesCount] = useState(null);
  // GET /users/me/schedule payload; null hides the panel (not loaded, or failed)
  const [mySchedule, setMySchedule] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [teamsCount, setTeamsCount] = useState(0);
  const [dmUnreadCount, setDmUnreadCount] = useState(0);
  const [followStats, setFollowStats] = useState({ followerCount: 0, followingCount: 0 });
  // Leaderboard (ST-B07): top players from per-match player stats
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState({ loading: false, rows: [], error: null, sport: null });
  const [jerseyNumber, setJerseyNumber] = useState(null);
  const [nickname, setNickname] = useState('');

  // Fetch jersey number and nickname from the profile API on mount
  useEffect(() => {
    getUserProfile()
      .then((data) => {
        if (data?.jersey_number !== undefined) setJerseyNumber(data.jersey_number);
        if (data?.nickname) setNickname(data.nickname);
        // Sync avatar_url from API to keep dashboard in sync with profile page
        if (data?.avatar_url) {
          setProfile(prev => ({ ...prev, avatar_url: data.avatar_url }));
        }
        // Sync to localStorage for offline use
        try {
          const stored = localStorage.getItem('user');
          if (stored) {
            const parsed = JSON.parse(stored);
            localStorage.setItem('user', JSON.stringify({
              ...parsed,
              jersey_number: data?.jersey_number ?? parsed.jersey_number,
              nickname: data?.nickname ?? parsed.nickname,
              avatar_url: data?.avatar_url ?? parsed.avatar_url,
              avatarUrl: data?.avatar_url ?? parsed.avatarUrl,
            }));
          }
        } catch (_) {}
      })
      .catch(() => {
        // Non-critical — read from localStorage as fallback
        try {
          const stored = localStorage.getItem('user');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.jersey_number !== undefined) setJerseyNumber(parsed.jersey_number);
            if (parsed.nickname) setNickname(parsed.nickname);
          }
        } catch (_) {}
      });
  }, []);

  const navigate = useNavigate();

  // Verified current user (AuthProvider re-fetches /users/profile on load),
  // used for role/permission checks instead of possibly-stale localStorage.
  const { user: authUser } = useAuth();

  // WebSocket context exposes `connected` / `connectionState` / `addEventListener`
  const { connected, connectionState, addEventListener } = useWebSocket();

  // Real-time notifications. The backend pushes `notification:new` to the
  // user's own socket ({ notification, playSound }). Match-room events such as
  // `live_score_update` only reach sockets that joined that match room, so the
  // dashboard doesn't subscribe to them (the old live-events panel was dead).
  useEffect(() => {
    if (!connected || !addEventListener) return undefined;
    return addEventListener('notification:new', (payload) => {
      const notification = payload?.notification;
      if (!notification) return;
      setNotifications(prev => [notification, ...prev.slice(0, 9)]);
      logger.info('Received notification', { type: notification.type });
    });
  }, [connected, addEventListener]);

  // Leaderboard (ST-B07): top players in one of the user's sports (first by
  // default; the modal switches between them), from player_stats
  const openLeaderboard = async (sport = userSports[0]) => {
    setShowLeaderboard(true);
    setLeaderboard({ loading: true, rows: [], error: null, sport: sport || null });
    try {
      const sportId = sport?.id;
      const resp = await teamService.getLeaderboards({ ...(sportId ? { sportId } : {}), limit: 5 });
      setLeaderboard({ loading: false, rows: normalizeLeaderboard(resp, 5), error: null, sport: sport || null });
    } catch (err) {
      setLeaderboard({ loading: false, rows: [], error: 'Could not load the leaderboard.', sport: sport || null });
    }
  };

  // Live matches near the user, the same list the Live Matches tile opens
  const { filterParams, isFiltersReady, needsLocation } = useUserFilters();
  useEffect(() => {
    if (!isFiltersReady) return undefined;
    if (needsLocation) {
      setLiveMatchesCount(null);
      return undefined;
    }
    let cancelled = false;
    matchService.getAllMatches({ ...filterParams, status: 'in_progress', view: 'upcoming', limit: 100 })
      .then((liveResponse) => {
        if (cancelled) return;
        const data = liveResponse?.data || {};
        const live = data.matches;
        setLiveMatchesCount(Array.isArray(live) ? (data.total ?? live.length) : null);
      })
      .catch(() => {
        if (!cancelled) setLiveMatchesCount(null);
      });
    return () => { cancelled = true; };
  }, [filterParams, isFiltersReady, needsLocation]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let currentUserData = null;
        
        // Small delay to ensure cookies are fully set after login redirect
        await new Promise(resolve => setTimeout(resolve, 100));

        // localStorage user, overlaid with the verified AuthContext user so
        // id/role/permissions are never taken from a stale cached copy
        const storedUser = localStorage.getItem("user");
        if (storedUser || authUser) {
          const userData = { ...(storedUser ? JSON.parse(storedUser) : {}), ...(authUser || {}) };
          currentUserData = userData;
          setProfile(userData);
          logger.info("Dashboard loaded user from localStorage", {
            userName: userData.name,
          });
        } else {
          // Fall back to API call if no stored user data
          try {
            const userData = await apiService.getUserProfile();
            currentUserData = userData.data || userData;
            setProfile(currentUserData);

            // Store user data for future use
            localStorage.setItem(
              "user",
              JSON.stringify(currentUserData)
            );
            logger.info("Dashboard loaded user from API", {
              userName: userData.name,
            });
          } catch (profileError) {
            // If profile fails, we might not be authenticated
            // But don't redirect here - let the interceptor handle it
          }
        }

        // Load user's sports
        let loadedSports = [];
        try {
          logger.info("Fetching user sports...");
          const sportsData = await getUserSports();
          loadedSports = sportsData.data || [];
          setUserSports(loadedSports);

          // Show onboarding if user has no sports and hasn't dismissed it
          if ((!sportsData.data || sportsData.data.length === 0) && !localStorage.getItem(ONBOARDING_KEY)) {
            setShowOnboarding(true);
          }
        } catch (sportsError) {
          logger.error("Sports loading failed", { error: sportsError.message, status: sportsError.response?.status });
          // Don't fail entire dashboard if sports fail
          setUserSports([]);
        }

        // My schedule: the next 2 weeks (PR 7a)
        try {
          const scheduleResponse = await apiClient.get('/users/me/schedule', { params: { days: 14 } });
          setMySchedule(scheduleResponse.data?.data || null);
        } catch (scheduleError) {
          setMySchedule(null);
        }

        // Load teams count (user's joined teams)
        try {
          logger.info("Fetching teams count...");
          const teamsResponse = await apiClient.get('/teams/my-teams');
          const teams = teamsResponse.data?.data?.teams || [];
          setTeamsCount(teams.length);
        } catch (teamsError) {
          logger.error("Teams loading failed", { error: teamsError.message, status: teamsError.response?.status });
          setTeamsCount(0);
        }

        // Unread direct messages for the Messages tile
        try {
          const dm = await directMessageService.getUnreadCount();
          setDmUnreadCount(Number(dm?.data?.unread_count) || 0);
        } catch (dmError) {
          setDmUnreadCount(0);
        }

        // Load follow stats (followers / following count for the current user)
        try {
          if (currentUserData?.id) {
            const stats = await followService.getMyStats(currentUserData.id);
            setFollowStats({
              followerCount:  stats.data?.followerCount  ?? 0,
              followingCount: stats.data?.followingCount ?? 0,
            });
          }
        } catch (followError) {
          // Non-critical — don't block dashboard load
        }
        
      } catch (err) {
        logger.error("Dashboard data loading failed", { 
          error: err.message, 
          status: err.response?.status,
          url: err.config?.url 
        });
        
        // CRITICAL: Don't throw, don't redirect, just log the error
        // The 401 interceptor will handle auth issues
      } finally {
        setLoading(false);
      }
    };

    // Re-enabled with safe error handling - won't trigger logout loops
    fetchData().catch(() => {
      setLoading(false);
    });

    // Listen for avatar updates from Profile page
    const handleAvatarUpdate = (event) => {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const updatedUser = JSON.parse(storedUser);
        setProfile(updatedUser);
      }
    };

    window.addEventListener('avatarUpdated', handleAvatarUpdate);
    return () => window.removeEventListener('avatarUpdated', handleAvatarUpdate);
    // Phase 1.1: Removed token dependency - auth via HttpOnly cookies
    // Runs once on mount; authUser is read at that point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSportSelect = (sport) => {
    const slug = (sport.name || '').toLowerCase().trim().replace(/\s+/g, '_');
    navigate(`/stats/${slug}`);
  };

  // Role/permission checks use the verified AuthContext user when available
  const roleUser = authUser || profile;

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="modern-dashboard">
      {showOnboarding && (
        <SportsOnboardingModal
          onComplete={(ids) => {
            setShowOnboarding(false);
            if (ids.length > 0) {
              // Refresh sports list
              getUserSports().then((d) => setUserSports(d.data || [])).catch(() => {});
            }
          }}
        />
      )}
      {/* Floating Top Navigation */}
      <div className="floating-nav">
        <div className="floating-nav-container">
          {/* Floating Logo/Title */}
          <div className="floating-logo">
            <h1 className="floating-title">Sportifide</h1>
          </div>

          {/* Floating Navigation Buttons */}
          <div className="floating-nav-buttons">
            {/* Bell icon — combines challenge + notification counts */}
            <NotificationBell />

            <InstallAppButton />

            <button
              onClick={() => navigate("/profile")}
              className="floating-nav-btn profile-floating"
            >
              <span className="nav-icon">👤</span>
              Profile
            </button>

            {/* Admin Access */}
            {(roleUser.role === "super_admin" ||
              roleUser.roleName === "super_admin" ||
              roleUser.role_name === "super_admin" ||
              (Array.isArray(roleUser.permissions) &&
                roleUser.permissions.some((p) =>
                  [
                    "sports.write",
                    "sports.admin",
                    "sports_management",
                  ].includes(typeof p === "string" ? p : p.name)
                ))) && (
              <button
                onClick={() => navigate("/sports-admin")}
                className="floating-nav-btn admin-floating"
                title="Sports Administration"
              >
                <span className="nav-icon">⚙️</span>
                Admin
              </button>
            )}

            {/* Organization Sports Access */}
            {(roleUser.organizationId ||
              (Array.isArray(roleUser.permissions) &&
                roleUser.permissions.some((p) =>
                  ["organization.sports"].includes(
                    typeof p === "string" ? p : p.name
                  )
                ))) && (
              <button
                onClick={() => navigate("/organization-sports")}
                className="floating-nav-btn org-floating"
                title="Organization Sports Management"
              >
                <span className="nav-icon">🏟️</span>
                Org Sports
              </button>
            )}

            <button
              onClick={onLogout}
              className="floating-nav-btn logout-floating"
            >
              <span className="nav-icon">🚪</span>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Dashboard Container */}
      <div className="dashboard-main">
        {/* Left Side - Character/Avatar Section */}
        <div className="dashboard-left">
          {/* 3D Standing Avatar - No Card Background */}
          <div className="character-3d-container">
            <div className="character-shadow"></div>
            <div className="character-3d-avatar">
              <img
                src={
                  profile.avatar_url ||
                  profile.avatarUrl ||
                  '/assets/profile_avatar/sports_avatar.png'
                }
                alt="Player Avatar"
                className="character-3d-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div
                className="character-3d-placeholder"
                style={{ display: "none" }}
              >
                <div className="avatar-3d-icon">
                  {roleUser.role_name === "super_admin" ||
                  roleUser.role === "super_admin" ||
                  roleUser.roleName === "super_admin"
                    ? "🏆"
                    : "⚽"}
                </div>
              </div>
            </div>
          </div>

          {/* Character Info Cards - Separate from Avatar */}
          <div className="character-info-section">
            {/* Name + Jersey side by side */}
            <div className="character-info-primary">
              <div className="player-name-card">
                <h2 className="player-name">
                  {profile.firstName || profile.name || "Player"}
                </h2>
                {nickname && (
                  <p className="player-nickname" style={{ color: '#ffffff', fontSize: '0.85rem', margin: '0 0 0.2rem' }}>
                    "{nickname}"
                  </p>
                )}
                <p className="player-subtitle">Ready for the next game?</p>
              </div>

              <div className="jersey-badge">
                <div className="jersey-number">{jerseyNumber !== null ? jerseyNumber : '–'}</div>
                <span className="jersey-label">Jersey</span>
              </div>
            </div>

            {/* Player Stats Cards */}
            <div className="player-stats-grid">
              <div className="stat-card-3d">
                <div className="stat-value">{userSports.length}</div>
                <div className="stat-label">Sports</div>
              </div>
              <div
                className="stat-card-3d stat-card-3d--clickable"
                onClick={() => navigate(`/players/${profile.short_code || profile.uuid || profile.id}/followers`)}
                title="View your followers"
                style={{ cursor: 'pointer' }}
              >
                <div className="stat-value">{followStats.followerCount}</div>
                <div className="stat-label">Followers</div>
              </div>
              <div
                className="stat-card-3d stat-card-3d--clickable"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/players/${profile.short_code || profile.uuid || profile.id}/following`)}
                title="People you are following"
              >
                <div className="stat-value">{followStats.followingCount}</div>
                <div className="stat-label">Following</div>
              </div>
            </div>

            {/* Player Search - below stats */}
            <PlayerSearch />
          </div>
        </div>

        {/* Right Side - Dashboard Modules */}
        <div className="dashboard-right">
          {mySchedule && <MySchedule schedule={mySchedule} />}

          {needsLocation && <FilterBar showSports={false} />}

          {/* 2×4 compact module grid: left col = Leagues/Tournaments/Drop-in/Teams, right col = My Matches/Live Matches/My Sports/Messages */}
          <div className="dashboard-modules">

            {/* Row 1, Col 1 – Leagues */}
            <div className="floating-module leagues-floating" onClick={() => navigate("/leagues")}>
              <div className="floating-icon">🏟️</div>
              <div className="floating-content">
                <h3 className="floating-module-title">Leagues</h3>
                <p className="floating-description">Season-long competitions</p>
              </div>
            </div>

            {/* Row 1, Col 2 – My Matches */}
            <div className="floating-module my-matches-floating" onClick={() => navigate("/matches/manage")}>
              <div className="floating-icon">📋</div>
              <div className="floating-content">
                <h3 className="floating-module-title">My Matches</h3>
                <p className="floating-description">Track your matches</p>
              </div>
            </div>

            {/* Row 2, Col 1 – Tournaments */}
            <div className="floating-module tournaments-floating" onClick={() => navigate("/tournaments")}>
              <div className="floating-icon">🏆</div>
              <div className="floating-content">
                <h3 className="floating-module-title">Tournaments</h3>
                <p className="floating-description">Exciting competitions</p>
              </div>
            </div>

            {/* Row 2, Col 2 – Live Matches */}
            <div className="floating-module matches-floating" onClick={() => navigate("/matches?status=in_progress")}>
              <div className="floating-icon">
                ⚽{liveMatchesCount > 0 && <span className="live-indicator">🔴</span>}
              </div>
              <div className="floating-content">
                <h3 className="floating-module-title">
                  Live Matches&nbsp;
                  <span className="connection-status" title={`Real-time connection: ${connectionState || 'disconnected'}`}>
                    {connected ? '🟢' : (connectionState === 'connecting' || connectionState === 'reconnecting') ? '🟡' : '🔴'}
                  </span>
                </h3>
                <p className="floating-description">Real-time updates</p>
                {liveMatchesCount !== null && (
                  <div className="floating-stats">{liveMatchesCount} Live now</div>
                )}
              </div>
            </div>

            {/* Row 3, Col 1 – Drop-in Play */}
            <div className="floating-module dropin-floating" onClick={() => navigate("/drop-in")}>
              <div className="floating-icon">🏃</div>
              <div className="floating-content">
                <h3 className="floating-module-title">Drop-in Play</h3>
                <p className="floating-description">Open-play sessions</p>
              </div>
            </div>

            {/* Row 3, Col 2 – My Stats */}
            <div
              className="floating-module sports-floating"
              onClick={() => userSports.length > 0
                ? navigate('/stats')
                : navigate("/sports", { state: { from: "dashboard" } })
              }
            >
              <div className="floating-icon">📊</div>
              <div className="floating-content">
                <h3 className="floating-module-title">My Stats</h3>
                {userSports.length > 0 ? (
                  <div className="floating-sport-pills">
                    {userSports.slice(0, 3).map(s => (
                      <span
                        key={s.id}
                        className="sport-pill"
                        onClick={e => {
                          e.stopPropagation();
                          handleSportSelect(s);
                        }}
                      >
                        {s.name}
                      </span>
                    ))}
                    {userSports.length > 3 && (
                      <span className="sport-pill sport-pill--more">+{userSports.length - 3}</span>
                    )}
                  </div>
                ) : (
                  <div className="floating-stats">No sports yet&nbsp;• Add some</div>
                )}
              </div>
            </div>

            {/* Row 4, Col 1 – Teams */}
            <div className="floating-module teams-floating" onClick={() => navigate("/teams")}>
              <div className="floating-icon">👥</div>
              <div className="floating-content">
                <h3 className="floating-module-title">Teams</h3>
                <p className="floating-description">Connect with teammates</p>
                <div className="floating-stats">{teamsCount} Joined</div>
              </div>
            </div>

            {/* Row 4, Col 2 – Leaderboard (top players in your first sport) */}
            <div className="floating-module" onClick={() => openLeaderboard()}>
              <div className="floating-icon">🏅</div>
              <div className="floating-content">
                <h3 className="floating-module-title">Leaderboard</h3>
                <p className="floating-description">Top players &amp; rankings</p>
                <div className="floating-stats">{userSports[0]?.name ? `Top ${userSports[0].name} players` : 'Top players'}</div>
              </div>
            </div>

            {/* Row 5, Col 1 – Messages */}
            <div className="floating-module" onClick={() => navigate("/messages")}>
              <div className="floating-icon">💬</div>
              <div className="floating-content">
                <h3 className="floating-module-title">Messages</h3>
                <p className="floating-description">Direct messages</p>
                <div className="floating-stats">
                  {dmUnreadCount > 0 ? `${dmUnreadCount} Unread` : 'No unread messages'}
                </div>
              </div>
            </div>

            {/* Row 5, Col 2 – Quick Score (no login needed, not persisted) */}
            <div className="floating-module" onClick={() => navigate("/quick-score")}>
              <div className="floating-icon">⚡</div>
              <div className="floating-content">
                <h3 className="floating-module-title">Quick Score</h3>
                <p className="floating-description">Score a casual game, no setup</p>
                <div className="floating-stats">Instant &amp; Unsaved</div>
              </div>
            </div>

          </div>{/* end dashboard-modules grid */}

          {/* Quick Action Buttons – spread evenly below the grid */}
          <div className="floating-actions">
            <button
              className="floating-action-btn primary-floating"
              onClick={() => navigate("/matches/create")}
            >
              <span className="action-icon">⚡</span>
              Create Match
            </button>
            <button
              className="floating-action-btn secondary-floating"
              onClick={() => navigate("/sports", { state: { from: "dashboard" } })}
            >
              <span className="action-icon">⚙️</span>
              Manage Sports
            </button>
          </div>

          {/* Real-time Notifications Panel */}
          {notifications.length > 0 && (
            <div className="notifications-panel">
              <h3 className="panel-title">
                🔔 Live Notifications
                <span className="notification-count">{notifications.length}</span>
              </h3>
              <div className="notifications-list">
                {notifications.slice(0, 5).map((notification, index) => (
                  <div key={notification.id || index} className="notification-item">
                    <div className="notification-type">🔔</div>
                    <div className="notification-content">
                      {notification.title && (
                        <div className="notification-title">{notification.title}</div>
                      )}
                      <div className="notification-message">{notification.message}</div>
                      {notification.created_at && (
                        <div className="notification-time">
                          {new Date(notification.created_at).toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>{/* end dashboard-right */}
      </div>{/* end dashboard-main */}

      {showLeaderboard && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="leaderboard-title" onClick={() => setShowLeaderboard(false)}>
          <div className="confirm-modal leaderboard-modal" onClick={(e) => e.stopPropagation()}
            style={{ background: 'rgba(15,20,30,0.97)', color: '#e2e8f0', padding: '1.25rem', borderRadius: '0.75rem', maxWidth: '420px', width: '100%' }}>
            <h3 id="leaderboard-title">🏅 Leaderboard{leaderboard.sport?.name ? ` — ${leaderboard.sport.name}` : ''}</h3>
            {userSports.length > 1 && (
              <div role="tablist" aria-label="Sport" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', margin: '0.5rem 0' }}>
                {userSports.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="tab"
                    aria-selected={leaderboard.sport?.id === s.id}
                    onClick={() => openLeaderboard(s)}
                    style={{
                      padding: '0.25rem 0.7rem', borderRadius: '999px', cursor: 'pointer', fontSize: '0.85rem',
                      border: '1px solid rgba(148,163,184,0.4)',
                      background: leaderboard.sport?.id === s.id ? '#3b82f6' : 'transparent',
                      color: leaderboard.sport?.id === s.id ? '#fff' : '#e2e8f0',
                    }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
            {leaderboard.loading && <p>Loading…</p>}
            {leaderboard.error && <p style={{ color: '#f87171' }}>{leaderboard.error}</p>}
            {!leaderboard.loading && !leaderboard.error && leaderboard.rows.length === 0 && (
              <p>No player stats yet. Matches scored live in Sportifide count here; league results show on each league's standings.</p>
            )}
            {leaderboard.rows.length > 0 && (
              <ol className="leaderboard-list" data-testid="leaderboard-list" style={{ paddingLeft: '1.25rem' }}>
                {leaderboard.rows.map((r) => (
                  <li key={r.user_id ?? r.name} style={{ margin: '0.35rem 0' }}>
                    <strong>{r.name}</strong>{' '}
                    <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                      {r.wins}W · {r.losses}L · {r.games_played} played
                    </span>
                  </li>
                ))}
              </ol>
            )}
            <button type="button" className="conflict-btn conflict-btn-revise" onClick={() => setShowLeaderboard(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
